<template>
  <div class="page-wrapper">
    <HeaderComponent />

    <main class="main-content">
      <!-- Page Hero -->
      <section class="page-hero">
        <div class="container">
          <div class="hero-content">
            <nav class="breadcrumb-nav">
              <a href="/" class="breadcrumb-link">Home</a>
              <span class="breadcrumb-separator">/</span>
              <span class="breadcrumb-current">Health Certificates</span>
            </nav>
            <h1 class="page-title">Food Hygiene Certificate</h1>
            <p class="page-subtitle">Apply for food handling and hygiene certificates for your establishment</p>
          </div>

          <!-- Progress Steps -->
          <div class="progress-stepper" v-if="currentStep < 5">
            <div
                v-for="(step, index) in steps"
                :key="index"
                class="step-item"
                :class="{
                active: currentStep === index + 1,
                completed: currentStep > index + 1
              }"
            >
              <div class="step-connector" v-if="index > 0"></div>
              <div class="step-circle">
                <svg v-if="currentStep > index + 1" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                <span v-else>{{ index + 1 }}</span>
              </div>
              <span class="step-label">{{ step }}</span>
            </div>
          </div>
        </div>
      </section>

      <!-- Main Content -->
      <section class="content-section">
        <div class="container">
          <div class="content-grid">
            <!-- Left Side - Info Panel -->
            <div class="info-panel">
              <div class="info-card">
                <div class="info-card-header">
                  <div class="info-icon">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                      <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
                    </svg>
                  </div>
                  <h3>Health Certificates</h3>
                </div>
                <p class="info-description">
                  Food hygiene certificates are required for all food handling establishments to ensure public health and safety standards are met.
                </p>

                <div class="info-features">
                  <div class="feature-item">
                    <div class="feature-icon green">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                        <polyline points="22 4 12 14.01 9 11.01"></polyline>
                      </svg>
                    </div>
                    <div>
                      <strong>Quick Processing</strong>
                      <p>Get your certificate within days</p>
                    </div>
                  </div>

                  <div class="feature-item">
                    <div class="feature-icon blue">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                        <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                      </svg>
                    </div>
                    <div>
                      <strong>Secure Process</strong>
                      <p>Your data is protected</p>
                    </div>
                  </div>

                  <div class="feature-item">
                    <div class="feature-icon purple">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                        <polyline points="14 2 14 8 20 8"></polyline>
                      </svg>
                    </div>
                    <div>
                      <strong>Digital Certificate</strong>
                      <p>Download and print anytime</p>
                    </div>
                  </div>
                </div>

                <!-- Quick Links Menu -->
                <div class="quick-links">
                  <h4 class="quick-links-title">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <rect x="3" y="3" width="7" height="7"></rect>
                      <rect x="14" y="3" width="7" height="7"></rect>
                      <rect x="14" y="14" width="7" height="7"></rect>
                      <rect x="3" y="14" width="7" height="7"></rect>
                    </svg>
                    Quick Actions
                  </h4>

                  <router-link :to="{ name: 'print-food-hygiene-licence' }" class="quick-link-item">
                    <div class="quick-link-icon print">
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <polyline points="6 9 6 2 18 2 18 9"></polyline>
                        <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
                        <rect x="6" y="14" width="12" height="8"></rect>
                      </svg>
                    </div>
                    <div class="quick-link-content">
                      <strong>Print Food Hygiene Licence</strong>
                      <p>View and print your existing licence</p>
                    </div>
                    <div class="quick-link-arrow">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <polyline points="9 18 15 12 9 6"></polyline>
                      </svg>
                    </div>
                  </router-link>

                </div>

                <!-- Who Needs This -->
                <div class="who-needs">
                  <h4>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <circle cx="12" cy="12" r="10"></circle>
                      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                      <line x1="12" y1="17" x2="12.01" y2="17"></line>
                    </svg>
                    Who Needs This Certificate?
                  </h4>
                  <ul>
                    <li>Restaurants & Hotels</li>
                    <li>Food Kiosks & Cafeterias</li>
                    <li>Butcheries & Meat Shops</li>
                    <li>Bakeries & Confectioneries</li>
                    <li>Supermarkets & Grocery Stores</li>
                    <li>Food Processing Plants</li>
                    <li>School & Hospital Canteens</li>
                  </ul>
                </div>

                <!-- Help Contact -->
                <div class="help-contact">
                  <h4>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                    </svg>
                    Need Help?
                  </h4>
                  <div class="contact-info">
                    <div class="contact-item">
                      <span>{{ county?.contact || '0700-000-000' }}</span>
                    </div>
                    <div class="contact-item">
                      <span>{{ county?.email || 'health@county.go.ke' }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Right Side - Form Panel -->
            <div class="form-panel">
              <!-- Error Alert -->
              <div v-if="error" class="alert alert-error">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="15" y1="9" x2="9" y2="15"></line>
                  <line x1="9" y1="9" x2="15" y2="15"></line>
                </svg>
                <span>{{ error }}</span>
                <button @click="error = ''" class="alert-close">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>

              <transition name="slide-fade" mode="out-in">
                <!-- Step 1: Find or Register Business -->
                <div v-if="currentStep === 1" key="step1" class="form-card">
                  <div class="form-card-header">
                    <div class="step-badge">
                      <span class="step-number">1</span>
                      <span class="step-text">Find Your Business</span>
                    </div>
                  </div>

                  <div class="form-card-body">
                    <p class="form-intro">
                      Search for your existing registered business or register a new establishment.
                    </p>

                    <!-- Search Method Selection -->
                    <div class="method-selection">
                      <label
                          class="method-card"
                          :class="{ selected: searchMethod === 'search' }"
                      >
                        <input type="radio" v-model="searchMethod" value="search" class="hidden-radio" />
                        <div class="method-icon">
                          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                            <circle cx="11" cy="11" r="8"></circle>
                            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                          </svg>
                        </div>
                        <div class="method-content">
                          <strong>Search Existing Business</strong>
                          <p>Find your registered business by Business ID</p>
                        </div>
                      </label>

                      <label
                          class="method-card"
                          :class="{ selected: searchMethod === 'register' }"
                      >
                        <input type="radio" v-model="searchMethod" value="register" class="hidden-radio" />
                        <div class="method-icon green">
                          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                            <line x1="12" y1="5" x2="12" y2="19"></line>
                            <line x1="5" y1="12" x2="19" y2="12"></line>
                          </svg>
                        </div>
                        <div class="method-content">
                          <strong>New Establishment</strong>
                          <p>Register a new food handling establishment</p>
                        </div>
                      </label>
                    </div>

                    <!-- Search Form -->
                    <div v-if="searchMethod === 'search'" class="search-section">
                      <div class="form-group">
                        <label class="form-label">Business ID <span class="text-danger">*</span></label>
                        <div class="input-with-button">
                          <input
                              type="text"
                              v-model="businessID"
                              class="form-input"
                              placeholder="Enter Business ID"
                              @keyup.enter="searchBusiness"
                          />
                          <button
                              type="button"
                              class="fetch-btn"
                              @click="searchBusiness"
                              :disabled="!businessID || searchingBusiness"
                          >
                            <span v-if="searchingBusiness" class="spinner small"></span>
                            <span v-else>Search</span>
                          </button>
                        </div>
                      </div>

                      <!-- Business Found Card -->
                      <div v-if="businessFound" class="business-found-card">
                        <div class="business-found-header">
                          <div class="found-icon">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                              <polyline points="22 4 12 14.01 9 11.01"></polyline>
                            </svg>
                          </div>
                          <div>
                            <h4>Business Found</h4>
                            <p>{{ businessDetails.businessID }}</p>
                          </div>
                        </div>
                        <div class="business-details-grid">
                          <div class="detail-row">
                            <span class="detail-label">Business Name</span>
                            <span class="detail-value">{{ businessDetails.businessName }}</span>
                          </div>
                          <div class="detail-row">
                            <span class="detail-label">Location</span>
                            <span class="detail-value">{{ businessDetails.wardName }}, {{ businessDetails.subCountyName }}</span>
                          </div>
                          <div class="detail-row">
                            <span class="detail-label">Physical Address</span>
                            <span class="detail-value">{{ businessDetails.physicalAddress || 'N/A' }}</span>
                          </div>
                          <div class="detail-row">
                            <span class="detail-label">Building/Premises</span>
                            <span class="detail-value">{{ businessDetails.buildingName || 'N/A' }}</span>
                          </div>
                          <div class="detail-row">
                            <span class="detail-label">Owner/Applicant</span>
                            <span class="detail-value">{{ businessDetails.fullNames }}</span>
                          </div>
                          <div class="detail-row">
                            <span class="detail-label">Phone</span>
                            <span class="detail-value">{{ businessDetails.ownerPhone }}</span>
                          </div>
                        </div>
                        <button class="btn btn-primary w-100 mt-3" @click="proceedWithBusiness">
                          Continue with this Business
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <line x1="5" y1="12" x2="19" y2="12"></line>
                            <polyline points="12 5 19 12 12 19"></polyline>
                          </svg>
                        </button>
                      </div>

                      <!-- Business Not Found -->
                      <div v-if="businessNotFound" class="not-found-card">
                        <div class="not-found-icon">
                          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="12" y1="8" x2="12" y2="12"></line>
                            <line x1="12" y1="16" x2="12.01" y2="16"></line>
                          </svg>
                        </div>
                        <h4>Business Not Found</h4>
                        <p>No business found with this ID. You can register a new establishment instead.</p>
                        <button class="btn btn-secondary" @click="searchMethod = 'register'">
                          Register New Establishment
                        </button>
                      </div>
                    </div>

                    <!-- New Registration Form -->
                    <div v-if="searchMethod === 'register'" class="register-section">
                      <div class="form-group">
                        <label class="form-label">Name of Establishment <span class="text-danger">*</span></label>
                        <input
                            type="text"
                            v-model="businessDetails.businessName"
                            class="form-input"
                            placeholder="e.g., ABC Restaurant"
                        />
                      </div>

                      <div class="form-row">
                        <div class="form-group">
                          <label class="form-label">Building/Premises Name</label>
                          <input
                              type="text"
                              v-model="businessDetails.buildingName"
                              class="form-input"
                              placeholder="e.g., City Mall"
                          />
                        </div>
                        <div class="form-group">
                          <label class="form-label">Plot Number</label>
                          <input
                              type="text"
                              v-model="businessDetails.plotNumber"
                              class="form-input"
                              placeholder="Enter plot number"
                          />
                        </div>
                      </div>

                      <div class="form-row">
                        <div class="form-group">
                          <label class="form-label">Sub County <span class="text-danger">*</span></label>
                          <div class="select-wrapper">
                            <select v-model="selectedSubCounty" class="form-select" @change="onSubCountyChange">
                              <option value="">-- Select Sub County --</option>
                              <option v-for="sc in subCounties" :key="sc.subCountyID" :value="sc">
                                {{ sc.subCountyName }}
                              </option>
                            </select>
                            <span class="select-icon">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <polyline points="6 9 12 15 18 9"></polyline>
                              </svg>
                            </span>
                          </div>
                        </div>
                        <div class="form-group">
                          <label class="form-label">Ward <span class="text-danger">*</span></label>
                          <div class="select-wrapper">
                            <select v-model="selectedWard" class="form-select" :disabled="!selectedSubCounty">
                              <option value="">-- Select Ward --</option>
                              <option v-for="w in wards" :key="w.wardID" :value="w">
                                {{ w.wardName }}
                              </option>
                            </select>
                            <span class="select-icon">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <polyline points="6 9 12 15 18 9"></polyline>
                              </svg>
                            </span>
                          </div>
                        </div>
                      </div>

                      <div class="form-group">
                        <label class="form-label">Physical Address <span class="text-danger">*</span></label>
                        <textarea
                            v-model="businessDetails.physicalAddress"
                            class="form-textarea"
                            rows="2"
                            placeholder="Enter the physical location of your establishment"
                        ></textarea>
                      </div>

                      <div class="form-row">
                        <div class="form-group">
                          <label class="form-label">Applicant's Full Name <span class="text-danger">*</span></label>
                          <input
                              type="text"
                              v-model="businessDetails.fullNames"
                              class="form-input"
                              placeholder="Enter your full name"
                          />
                        </div>
                        <div class="form-group">
                          <label class="form-label">Postal Address</label>
                          <input
                              type="text"
                              v-model="businessDetails.postalAddress"
                              class="form-input"
                              placeholder="e.g., P.O Box 123-00100"
                          />
                        </div>
                      </div>

                      <div class="form-row">
                        <div class="form-group">
                          <label class="form-label">Phone Number <span class="text-danger">*</span></label>
                          <input
                              type="tel"
                              v-model="businessDetails.ownerPhone"
                              class="form-input"
                              placeholder="07XX XXX XXX"
                          />
                        </div>
                        <div class="form-group">
                          <label class="form-label">Email Address</label>
                          <input
                              type="email"
                              v-model="businessDetails.ownerEmail"
                              class="form-input"
                              placeholder="email@example.com"
                          />
                        </div>
                      </div>

                      <div class="form-group">
                        <label class="form-label">Nature of Business <span class="text-danger">*</span></label>
                        <input
                            type="text"
                            v-model="businessDetails.businessDes"
                            class="form-input"
                            placeholder="e.g., Restaurant, Butchery, Bakery"
                        />
                      </div>

                      <div class="form-actions">
                        <button
                            class="btn btn-primary btn-lg"
                            @click="registerAndContinue"
                            :disabled="!canProceedStep1Register || registering"
                        >
                          <span v-if="registering" class="spinner"></span>
                          <span v-else>Continue</span>
                          <svg v-if="!registering" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <line x1="5" y1="12" x2="19" y2="12"></line>
                            <polyline points="12 5 19 12 12 19"></polyline>
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Step 2: Select Certificate Category -->
                <div v-else-if="currentStep === 2" key="step2" class="form-card">
                  <div class="form-card-header">
                    <div class="step-badge">
                      <span class="step-number">2</span>
                      <span class="step-text">Certificate Category</span>
                    </div>
                    <button class="back-btn" @click="prevStep">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <line x1="19" y1="12" x2="5" y2="12"></line>
                        <polyline points="12 19 5 12 12 5"></polyline>
                      </svg>
                      Back
                    </button>
                  </div>

                  <div class="form-card-body">
                    <!-- Business Summary -->
                    <div class="business-summary">
                      <div class="summary-icon">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                          <polyline points="9 22 9 12 15 12 15 22"></polyline>
                        </svg>
                      </div>
                      <div>
                        <strong>{{ businessDetails.businessName }}</strong>
                        <p>{{ businessDetails.wardName }}, {{ businessDetails.subCountyName }}</p>
                      </div>
                    </div>

                    <div class="form-group">
                      <label class="form-label">Certificate Category <span class="text-danger">*</span></label>
                      <div class="select-wrapper">
                        <select v-model="selectedCategory" class="form-select" @change="onCategoryChange">
                          <option value="">-- Select Category --</option>
                          <option v-for="cat in incomeTypes" :key="cat.incomeTypeId" :value="cat">
                            {{ cat.incomeTypeDescription }}
                          </option>
                        </select>
                        <span class="select-icon">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <polyline points="6 9 12 15 18 9"></polyline>
                          </svg>
                        </span>
                      </div>
                    </div>

                    <div class="form-group" v-if="feesAndCharges.length > 0">
                      <label class="form-label">Sub Category <span class="text-danger">*</span></label>
                      <div class="select-wrapper">
                        <select v-model="selectedFee" class="form-select">
                          <option value="">-- Select Sub Category --</option>
                          <option v-for="fee in feesAndCharges" :key="fee.feeId" :value="fee">
                            {{ fee.feeDescription }} - KES {{ formatAmount(fee.unitFeeAmount) }}
                          </option>
                        </select>
                        <span class="select-icon">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <polyline points="6 9 12 15 18 9"></polyline>
                          </svg>
                        </span>
                      </div>
                    </div>

                    <div class="form-group">
                      <label class="form-label">Observations/Recommendations</label>
                      <textarea
                          v-model="observations"
                          class="form-textarea"
                          rows="3"
                          placeholder="Include any details for consideration during approval..."
                      ></textarea>
                    </div>

                    <!-- Other Applicable Charges - Multi-step Add -->
                    <div class="other-charges-section">
                      <div class="section-header-bar">
                        <div class="header-icon">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <line x1="12" y1="5" x2="12" y2="19"></line>
                            <line x1="5" y1="12" x2="19" y2="12"></line>
                          </svg>
                        </div>
                        <h4>Other Applicable Charges</h4>
                        <span class="charges-count">{{ otherChargeCategories.length }} categories available</span>
                      </div>

                      <div class="add-charge-form">
                        <div class="charge-step">
                          <span class="step-badge blue">Step 1</span>
                          <div class="form-group">
                            <label class="form-label">Select Category</label>
                            <div class="select-wrapper">
                              <select v-model="selectedOtherCategory" class="form-select" @change="onOtherCategoryChange">
                                <option :value="null">-- Select Category --</option>
                                <option v-for="cat in otherChargeCategories" :key="cat.incomeTypeId" :value="cat">
                                  {{ cat.incomeTypeDescription }}
                                </option>
                              </select>
                              <span class="select-icon">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                  <polyline points="6 9 12 15 18 9"></polyline>
                                </svg>
                              </span>
                            </div>
                          </div>
                        </div>

                        <div class="charge-step" v-if="selectedOtherCategory && feesAndChargesOther.length > 0">
                          <span class="step-badge green">Step 2</span>
                          <div class="form-group">
                            <label class="form-label">Select Specific Charge</label>
                            <div class="select-wrapper">
                              <select v-model="selectedCharge" class="form-select">
                                <option :value="null">-- Select a charge to add --</option>
                                <option v-for="charge in feesAndChargesOther" :key="charge.feeId" :value="charge">
                                  {{ charge.feeDescription }} - KES {{ formatAmount(charge.unitFeeAmount) }}
                                </option>
                              </select>
                              <span class="select-icon">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                  <polyline points="6 9 12 15 18 9"></polyline>
                                </svg>
                              </span>
                            </div>
                          </div>
                        </div>

                        <div class="charge-step-row" v-if="selectedOtherCategory && feesAndChargesOther.length > 0">
                          <div class="charge-step quantity-step">
                            <span class="step-badge purple">Step 3</span>
                            <div class="form-group">
                              <label class="form-label">Quantity</label>
                              <input
                                  type="number"
                                  v-model.number="chargeQuantity"
                                  class="form-input"
                                  min="1"
                                  placeholder="1"
                              />
                            </div>
                          </div>
                          <button
                              type="button"
                              class="btn btn-add-charge"
                              @click="addChargeToList"
                              :disabled="!selectedCharge"
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                              <line x1="12" y1="5" x2="12" y2="19"></line>
                              <line x1="5" y1="12" x2="19" y2="12"></line>
                            </svg>
                            Add Charge
                          </button>
                        </div>

                        <div v-if="selectedOtherCategory && feesAndChargesOther.length === 0" class="loading-charges">
                          <span class="spinner small blue"></span>
                          Loading charges for {{ selectedOtherCategory.incomeTypeDescription }}...
                        </div>
                      </div>

                      <!-- Added Charges Table -->
                      <div v-if="addedCharges.length > 0" class="added-charges-table">
                        <table class="charges-table">
                          <thead>
                          <tr>
                            <th>Charge Description</th>
                            <th class="text-center">Qty</th>
                            <th class="text-right">Unit Price</th>
                            <th class="text-right">Total</th>
                            <th class="text-center">Action</th>
                          </tr>
                          </thead>
                          <tbody>
                          <tr v-for="(item, index) in addedCharges" :key="index">
                            <td>{{ item.feeDescription }}</td>
                            <td class="text-center">{{ item.quantity }}</td>
                            <td class="text-right">KES {{ formatAmount(item.unitFeeAmount) }}</td>
                            <td class="text-right">KES {{ formatAmount(item.unitFeeAmount * item.quantity) }}</td>
                            <td class="text-center">
                              <button type="button" class="remove-charge-btn" @click="removeCharge(index)">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                  <polyline points="3 6 5 6 21 6"></polyline>
                                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                </svg>
                              </button>
                            </td>
                          </tr>
                          </tbody>
                          <tfoot>
                          <tr>
                            <th colspan="3">Total Other Charges ({{ addedCharges.length }} items)</th>
                            <th class="text-right">KES {{ formatAmount(totalAddedCharges) }}</th>
                            <th></th>
                          </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>

                    <!-- Fee Summary -->
                    <div class="fee-summary" v-if="selectedFee">
                      <div class="fee-row">
                        <span>Certificate Fee</span>
                        <span>KES {{ formatAmount(selectedFee.unitFeeAmount) }}</span>
                      </div>
                      <div class="fee-row" v-if="totalAddedCharges > 0">
                        <span>Other Charges ({{ addedCharges.length }} items)</span>
                        <span>KES {{ formatAmount(totalAddedCharges) }}</span>
                      </div>
                      <div class="fee-row total">
                        <span>Total Amount</span>
                        <span>KES {{ formatAmount(totalAmount) }}</span>
                      </div>
                    </div>

                    <div class="form-actions">
                      <button
                          class="btn btn-primary btn-lg"
                          @click="nextStep"
                          :disabled="!canProceedStep2"
                      >
                        Continue to Review
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <line x1="5" y1="12" x2="19" y2="12"></line>
                          <polyline points="12 5 19 12 12 19"></polyline>
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Step 3: Review & Submit -->
                <div v-else-if="currentStep === 3" key="step3" class="form-card">
                  <div class="form-card-header">
                    <div class="step-badge">
                      <span class="step-number">3</span>
                      <span class="step-text">Review & Submit</span>
                    </div>
                    <button class="back-btn" @click="prevStep">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <line x1="19" y1="12" x2="5" y2="12"></line>
                        <polyline points="12 19 5 12 12 5"></polyline>
                      </svg>
                      Back
                    </button>
                  </div>

                  <div class="form-card-body">
                    <p class="form-intro">
                      Review your application details before submitting.
                    </p>

                    <!-- Summary Sections -->
                    <div class="summary-section">
                      <div class="summary-header">
                        <h4>Establishment Details</h4>
                        <button class="edit-btn" @click="goToStep(1)">Edit</button>
                      </div>
                      <div class="summary-grid">
                        <div class="summary-item">
                          <span class="summary-label">Business Name</span>
                          <span class="summary-value">{{ businessDetails.businessName }}</span>
                        </div>
                        <div class="summary-item">
                          <span class="summary-label">Location</span>
                          <span class="summary-value">{{ businessDetails.wardName }}, {{ businessDetails.subCountyName }}</span>
                        </div>
                        <div class="summary-item">
                          <span class="summary-label">Physical Address</span>
                          <span class="summary-value">{{ businessDetails.physicalAddress || 'N/A' }}</span>
                        </div>
                        <div class="summary-item">
                          <span class="summary-label">Applicant</span>
                          <span class="summary-value">{{ businessDetails.fullNames }}</span>
                        </div>
                        <div class="summary-item">
                          <span class="summary-label">Phone</span>
                          <span class="summary-value">{{ businessDetails.ownerPhone }}</span>
                        </div>
                        <div class="summary-item">
                          <span class="summary-label">Email</span>
                          <span class="summary-value">{{ businessDetails.ownerEmail || 'N/A' }}</span>
                        </div>
                      </div>
                    </div>

                    <div class="summary-section">
                      <div class="summary-header">
                        <h4>Certificate Details</h4>
                        <button class="edit-btn" @click="goToStep(2)">Edit</button>
                      </div>
                      <div class="summary-grid">
                        <div class="summary-item">
                          <span class="summary-label">Category</span>
                          <span class="summary-value">{{ selectedCategory?.incomeTypeDescription }}</span>
                        </div>
                        <div class="summary-item">
                          <span class="summary-label">Sub Category</span>
                          <span class="summary-value">{{ selectedFee?.feeDescription }}</span>
                        </div>
                      </div>
                    </div>

                    <!-- Invoice Preview -->
                    <div class="invoice-preview">
                      <h4>Invoice Preview</h4>
                      <table class="invoice-table">
                        <thead>
                        <tr>
                          <th>Description</th>
                          <th class="text-center">Qty</th>
                          <th class="text-right">Unit Price</th>
                          <th class="text-right">Amount</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr>
                          <td>{{ selectedFee?.feeDescription }}</td>
                          <td class="text-center">1</td>
                          <td class="text-right">KES {{ formatAmount(selectedFee?.unitFeeAmount) }}</td>
                          <td class="text-right">KES {{ formatAmount(selectedFee?.unitFeeAmount) }}</td>
                        </tr>
                        <tr v-for="item in addedCharges" :key="item.feeId">
                          <td>{{ item.feeDescription }}</td>
                          <td class="text-center">{{ item.quantity }}</td>
                          <td class="text-right">KES {{ formatAmount(item.unitFeeAmount) }}</td>
                          <td class="text-right">KES {{ formatAmount(item.unitFeeAmount * item.quantity) }}</td>
                        </tr>
                        </tbody>
                        <tfoot>
                        <tr>
                          <th colspan="3">Total Payable</th>
                          <th class="text-right">KES {{ formatAmount(totalAmount) }}</th>
                        </tr>
                        </tfoot>
                      </table>
                    </div>

                    <!-- Payment Notice -->
                    <div class="payment-notice">
                      <div class="notice-icon">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <circle cx="12" cy="12" r="10"></circle>
                          <line x1="12" y1="16" x2="12" y2="12"></line>
                          <line x1="12" y1="8" x2="12.01" y2="8"></line>
                        </svg>
                      </div>
                      <div>
                        <strong>Payment Information</strong>
                        <p>After submission, you will receive an invoice via SMS to <strong>{{ businessDetails.ownerPhone }}</strong>. You can pay using M-Pesa or other available methods.</p>
                      </div>
                    </div>

                    <!-- Declaration -->
                    <div class="declaration-box">
                      <label class="declaration-label">
                        <input type="checkbox" v-model="declarationAccepted" />
                        <span class="checkbox-mark">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                        </span>
                        <span class="declaration-text">
                          I declare that the information provided is true and accurate. I understand that the certificate will be issued after payment and inspection.
                        </span>
                      </label>
                    </div>

                    <div class="form-actions">
                      <button
                          class="btn btn-success btn-lg"
                          @click="submitApplication"
                          :disabled="!declarationAccepted || submitting"
                      >
                        <span v-if="submitting" class="spinner white"></span>
                        <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                        {{ submitting ? 'Submitting...' : 'Submit Application' }}
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Step 4: Success -->
                <div v-else-if="currentStep === 4" key="step4" class="form-card success-card">
                  <div class="form-card-body">
                    <div class="success-content">
                      <div class="success-icon">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                          <polyline points="22 4 12 14.01 9 11.01"></polyline>
                        </svg>
                      </div>
                      <h2>Application Submitted!</h2>
                      <p class="success-message">Your food hygiene certificate application has been submitted successfully.</p>

                      <div class="invoice-card" v-if="billDetails">
                        <span class="invoice-label">Invoice Number</span>
                        <span class="invoice-number">{{ billDetails.billNo }}</span>
                        <div class="invoice-amount">
                          <span>Total Amount</span>
                          <strong>KES {{ formatAmount(billDetails.detailAmount) }}</strong>
                        </div>
                      </div>

                      <div class="notification-card">
                        <div class="notification-icon">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                          </svg>
                        </div>
                        <div class="notification-text">
                          <strong>Payment Instructions Sent</strong>
                          <p>Invoice details have been sent to <strong>{{ businessDetails.ownerPhone }}</strong></p>
                        </div>
                      </div>

                      <div class="next-steps">
                        <h4>What's Next?</h4>
                        <ul>
                          <li>
                            <span class="step-num">1</span>
                            <div>
                              <strong>Make Payment</strong>
                              <p>Pay using M-Pesa Paybill or visit our office</p>
                            </div>
                          </li>
                          <li>
                            <span class="step-num">2</span>
                            <div>
                              <strong>Inspection</strong>
                              <p>Health officer will inspect your establishment</p>
                            </div>
                          </li>
                          <li>
                            <span class="step-num">3</span>
                            <div>
                              <strong>Certificate Issued</strong>
                              <p>Collect or download your certificate</p>
                            </div>
                          </li>
                        </ul>
                      </div>

                      <div class="success-actions">
                        <button class="btn btn-primary" @click="printBill" v-if="billDetails">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <polyline points="6 9 6 2 18 2 18 9"></polyline>
                            <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
                            <rect x="6" y="14" width="12" height="8"></rect>
                          </svg>
                          Print Invoice
                        </button>
                        <button class="btn btn-secondary" @click="resetForm">
                          New Application
                        </button>
                        <router-link to="/" class="btn btn-outline">
                          Back to Home
                        </router-link>
                      </div>
                    </div>
                  </div>
                </div>
              </transition>
            </div>
          </div>
        </div>
      </section>
    </main>

    <FooterComponent />
  </div>
</template>

<script>
import { biller, health, trade, execute } from "@/api";
import HeaderComponent from "@/components/HeaderComponent.vue";
import FooterComponent from "@/components/FooterComponent.vue";

export default {
  name: "HealthCertificates",
  components: { HeaderComponent, FooterComponent },
  data() {
    return {
      county: null,
      currentStep: 1,
      steps: ['Business', 'Category', 'Review'],
      error: '',

      // Search/Registration
      searchMethod: '',
      businessID: '',
      searchingBusiness: false,
      businessFound: false,
      businessNotFound: false,
      registering: false,

      // Business details
      businessDetails: {
        id: null,
        businessID: null,
        businessName: '',
        subCountyID: null,
        subCountyName: '',
        wardID: null,
        wardName: '',
        plotNumber: '',
        physicalAddress: '',
        buildingName: '',
        postalAddress: '',
        postalCode: '',
        fullNames: '',
        ownerPhone: '',
        ownerEmail: '',
        businessDes: ''
      },

      // Location
      subCounties: [],
      wards: [],
      selectedSubCounty: null,
      selectedWard: null,

      // Categories
      incomeTypes: [],
      feesAndCharges: [],
      feesAndChargesOther: [],
      otherChargeCategories: [],
      selectedCategory: null,
      selectedFee: null,
      selectedOtherCategory: null,
      selectedCharge: null,
      chargeQuantity: 1,
      addedCharges: [],
      checkedItems: [],
      observations: '',

      // Submit
      declarationAccepted: false,
      submitting: false,
      billDetails: null,
      billInfo: []
    };
  },

  computed: {
    canProceedStep1Register() {
      return this.businessDetails.businessName &&
          this.selectedSubCounty &&
          this.selectedWard &&
          this.businessDetails.physicalAddress &&
          this.businessDetails.fullNames &&
          this.businessDetails.ownerPhone &&
          this.businessDetails.businessDes;
    },

    canProceedStep2() {
      return this.selectedCategory && this.selectedFee;
    },

    totalOtherCharges() {
      return this.checkedItems.reduce((sum, item) => sum + parseFloat(item.unitFeeAmount || 0), 0);
    },

    totalAddedCharges() {
      return this.addedCharges.reduce((sum, item) => sum + (parseFloat(item.unitFeeAmount || 0) * (item.quantity || 1)), 0);
    },

    totalAmount() {
      const baseFee = this.selectedFee ? parseFloat(this.selectedFee.unitFeeAmount) : 0;
      return baseFee + this.totalAddedCharges;
    }
  },

  watch: {
    selectedSubCounty(newVal) {
      if (newVal) {
        this.businessDetails.subCountyID = newVal.subCountyID;
        this.businessDetails.subCountyName = newVal.subCountyName;
        this.getWards(newVal.subCountyID);
      }
    },
    selectedWard(newVal) {
      if (newVal) {
        this.businessDetails.wardID = newVal.wardID;
        this.businessDetails.wardName = newVal.wardName;
      }
    }
  },

  mounted() {
    this.getCounty();
    this.getSubCounties();
    this.getIncomeTypes();
  },

  methods: {
    async getCounty() {
      try {
        const data = new FormData();
        data.append("function", "getCounty");
        const res = await execute(data, biller);
        if (res.data.success) {
          this.county = res.data.data.county;
        }
      } catch (e) { console.error(e); }
    },

    async getSubCounties() {
      try {
        const data = new FormData();
        data.append("function", "getSubCounty");
        const res = await execute(data, biller);
        if (res.data.success) {
          this.subCounties = res.data.data.subCounties;
        }
      } catch (e) { console.error(e); }
    },

    async getWards(subCountyID) {
      try {
        this.wards = [];
        this.selectedWard = null;
        const data = new FormData();
        data.append("function", "getWards");
        data.append("subCountyID", subCountyID);
        const res = await execute(data, biller);
        if (res.data.success) {
          this.wards = res.data.data.wards;
        }
      } catch (e) { console.error(e); }
    },

    onSubCountyChange() {
      this.selectedWard = null;
      this.wards = [];
    },

    async getIncomeTypes() {
      try {
        const data = new FormData();
        data.append("function", "getIncomeTypes");
        data.append("incomeTypePrefix", "HEALTH");
        const res = await execute(data, biller);
        if (res.data.success) {
          this.incomeTypes = res.data.data.incomeTypes;
          this.getIncomeTypesOther();
        }
      } catch (e) { console.error(e); }
    },

    async getIncomeTypesOther() {
      try {
        const data = new FormData();
        data.append("function", "getIncomeTypes");
        data.append("incomeTypePrefix", "HEALTH");
        const res = await execute(data, biller);
        if (res.data.success && res.data.data.incomeTypes.length > 0) {
          this.otherChargeCategories = res.data.data.incomeTypes;
        }
      } catch (e) { console.error(e); }
    },

    async onOtherCategoryChange() {
      if (!this.selectedOtherCategory) {
        this.feesAndChargesOther = [];
        return;
      }
      this.selectedCharge = null;
      this.feesAndChargesOther = [];
      try {
        const data = new FormData();
        data.append("function", "getFeesAndCharges");
        data.append("incomeTypeId", this.selectedOtherCategory.incomeTypeId);
        const res = await execute(data, biller);
        if (res.data.success) {
          this.feesAndChargesOther = res.data.data.feesAndCharges;
        }
      } catch (e) { console.error(e); }
    },

    addChargeToList() {
      if (!this.selectedCharge) return;

      const existingIndex = this.addedCharges.findIndex(c => c.feeId === this.selectedCharge.feeId);

      if (existingIndex >= 0) {
        this.addedCharges[existingIndex].quantity += (this.chargeQuantity || 1);
      } else {
        this.addedCharges.push({
          ...this.selectedCharge,
          quantity: this.chargeQuantity || 1
        });
      }

      this.selectedCharge = null;
      this.chargeQuantity = 1;
    },

    removeCharge(index) {
      this.addedCharges.splice(index, 1);
    },

    async onCategoryChange() {
      if (!this.selectedCategory) return;
      this.selectedFee = null;
      this.feesAndCharges = [];
      try {
        const data = new FormData();
        data.append("function", "getFeesAndCharges");
        data.append("incomeTypeId", this.selectedCategory.incomeTypeId);
        const res = await execute(data, biller);
        if (res.data.success) {
          this.feesAndCharges = res.data.data.feesAndCharges;
        }
      } catch (e) { console.error(e); }
    },

    async searchBusiness() {
      if (!this.businessID.trim()) return;
      this.searchingBusiness = true;
      this.businessFound = false;
      this.businessNotFound = false;
      this.error = '';

      try {
        const data = new FormData();
        data.append("function", "getBusiness");
        data.append("businessID", this.businessID.trim());
        const res = await execute(data, trade);

        if (res.data.success && res.data.data.business) {
          this.businessDetails = res.data.data.business;
          this.businessFound = true;
        } else {
          this.businessNotFound = true;
        }
      } catch (e) {
        this.error = 'Error searching for business. Please try again.';
      } finally {
        this.searchingBusiness = false;
      }
    },

    proceedWithBusiness() {
      this.currentStep = 2;
    },

    async registerAndContinue() {
      this.registering = true;
      this.error = '';

      try {
        const data = new FormData();
        data.append("function", "registerBusiness");
        data.append("business", JSON.stringify(this.businessDetails));
        const res = await execute(data, health);

        if (res.data.success) {
          this.businessDetails = res.data.data.business;
          this.currentStep = 2;
        } else {
          this.error = res.data.message || 'Failed to register business.';
        }
      } catch (e) {
        this.error = 'Error registering business. Please try again.';
      } finally {
        this.registering = false;
      }
    },

    nextStep() {
      if (this.currentStep < 4) {
        this.currentStep++;
      }
    },

    prevStep() {
      if (this.currentStep > 1) {
        this.currentStep--;
      }
    },

    goToStep(step) {
      this.currentStep = step;
    },

    formatAmount(amount) {
      if (!amount) return '0';
      return new Intl.NumberFormat('en-KE').format(parseFloat(amount));
    },

    async submitApplication() {
      this.submitting = true;
      this.error = '';

      try {
        const billItems = [];
        const sessionNames = sessionStorage.getItem("sessionNames") || this.businessDetails.fullNames;
        const sessionIdNo = sessionStorage.getItem("sessionIdNo") || '';
        const sessionPhone = sessionStorage.getItem("sessionPhoneNumber") || this.businessDetails.ownerPhone;

        if (this.selectedFee) {
          billItems.push({
            feeId: this.selectedFee.feeId,
            feeDescription: this.selectedFee.feeDescription,
            unitFeeAmount: this.selectedFee.unitFeeAmount,
            accountNo: this.selectedFee.accountNo,
            costCenterNo: this.selectedFee.costCenterNo,
            incomeTypeId: this.selectedFee.incomeTypeId,
            amount: this.selectedFee.unitFeeAmount,
            quantity: 1,
            customer: `Business: ${this.businessDetails.businessName}`,
            revenueStreamItem: this.businessDetails.businessName,
            subCountyID: this.businessDetails.subCountyID,
            subCountyName: this.businessDetails.subCountyName,
            wardID: this.businessDetails.wardID,
            wardName: this.businessDetails.wardName,
            idNo: sessionIdNo,
            phoneNumber: sessionPhone,
            names: sessionNames,
            customerPhoneNumber: this.businessDetails.ownerPhone,
            description: this.businessDetails.businessDes
          });
        }

        this.addedCharges.forEach(item => {
          billItems.push({
            feeId: item.feeId,
            feeDescription: item.feeDescription,
            unitFeeAmount: item.unitFeeAmount * item.quantity,
            accountNo: item.accountNo,
            costCenterNo: item.costCenterNo,
            incomeTypeId: item.incomeTypeId,
            amount: item.unitFeeAmount * item.quantity,
            quantity: item.quantity,
            customer: `Business: ${this.businessDetails.businessName}`,
            revenueStreamItem: this.businessDetails.businessName,
            subCountyID: this.businessDetails.subCountyID,
            subCountyName: this.businessDetails.subCountyName,
            wardID: this.businessDetails.wardID,
            wardName: this.businessDetails.wardName,
            idNo: sessionIdNo,
            phoneNumber: sessionPhone,
            names: sessionNames,
            customerPhoneNumber: this.businessDetails.ownerPhone,
            description: this.businessDetails.businessDes
          });
        });

        const data = new FormData();
        data.append("function", "generateBill3");
        data.append("billItem", JSON.stringify(billItems));

        const res = await execute(data, biller);

        if (res.data.success) {
          this.billDetails = res.data.data.bill.billDetails;
          this.billInfo = res.data.data.bill.billInfo;
          this.currentStep = 4;
        } else {
          this.error = res.data.message || 'Failed to submit application.';
        }
      } catch (e) {
        this.error = 'Error submitting application. Please try again.';
        console.error(e);
      } finally {
        this.submitting = false;
      }
    },

    printBill() {
      if (!this.billDetails?.billNo) return;
      localStorage['params'] = JSON.stringify({ billNo: this.billDetails.billNo });
      const routeData = this.$router.resolve({ name: 'bill' });
      window.open(routeData.href, '_blank');
    },

    resetForm() {
      this.currentStep = 1;
      this.searchMethod = '';
      this.businessID = '';
      this.businessFound = false;
      this.businessNotFound = false;
      this.businessDetails = {
        id: null,
        businessID: null,
        businessName: '',
        subCountyID: null,
        subCountyName: '',
        wardID: null,
        wardName: '',
        plotNumber: '',
        physicalAddress: '',
        buildingName: '',
        postalAddress: '',
        postalCode: '',
        fullNames: '',
        ownerPhone: '',
        ownerEmail: '',
        businessDes: ''
      };
      this.selectedSubCounty = null;
      this.selectedWard = null;
      this.selectedCategory = null;
      this.selectedFee = null;
      this.selectedOtherCategory = null;
      this.selectedCharge = null;
      this.chargeQuantity = 1;
      this.addedCharges = [];
      this.checkedItems = [];
      this.feesAndChargesOther = [];
      this.observations = '';
      this.declarationAccepted = false;
      this.billDetails = null;
      this.error = '';
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

.page-wrapper { min-height: 100vh; display: flex; flex-direction: column; background: #f8fafc; }
.main-content { flex: 1; padding-top: 72px; }

/* Hero */
.page-hero { background: linear-gradient(135deg, #0f4c81 0%, #0a3459 100%); padding: 48px 0 60px; color: white; }
.container { max-width: 1200px; margin: 0 auto; padding: 0 24px; }
.breadcrumb-nav { display: flex; align-items: center; gap: 8px; margin-bottom: 16px; font-size: 14px; }
.breadcrumb-link { color: rgba(255,255,255,0.7); text-decoration: none; }
.breadcrumb-link:hover { color: white; }
.breadcrumb-separator { color: rgba(255,255,255,0.4); }
.breadcrumb-current { color: white; }
.page-title { font-family: 'Plus Jakarta Sans', sans-serif; font-size: 32px; font-weight: 800; margin-bottom: 8px; }
.page-subtitle { opacity: 0.9; font-size: 16px; margin-bottom: 32px; }

/* Progress Stepper */
.progress-stepper { display: flex; justify-content: center; background: rgba(255,255,255,0.1); backdrop-filter: blur(10px); border-radius: 16px; padding: 24px 32px; }
.step-item { display: flex; align-items: center; gap: 12px; }
.step-connector { width: 60px; height: 3px; background: rgba(255,255,255,0.3); margin: 0 12px; border-radius: 2px; }
.step-item.completed .step-connector { background: #10b981; }
.step-circle { width: 40px; height: 40px; border-radius: 50%; background: rgba(255,255,255,0.2); display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 15px; }
.step-item.active .step-circle { background: white; color: #0f4c81; transform: scale(1.1); box-shadow: 0 4px 12px rgba(0,0,0,0.2); }
.step-item.completed .step-circle { background: #10b981; color: white; }
.step-label { font-size: 14px; font-weight: 500; opacity: 0.8; }
.step-item.active .step-label, .step-item.completed .step-label { opacity: 1; font-weight: 600; }

/* Content */
.content-section { padding: 48px 0 80px; }
.content-grid { display: grid; grid-template-columns: 1fr 1.5fr; gap: 32px; align-items: start; }

/* Info Panel */
.info-panel { position: sticky; top: 96px; }
.info-card { background: white; border-radius: 16px; padding: 32px; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1); }
.info-card-header { display: flex; align-items: center; gap: 16px; margin-bottom: 20px; }
.info-icon { width: 56px; height: 56px; background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%); border-radius: 14px; display: flex; align-items: center; justify-content: center; color: #0f4c81; }
.info-card-header h3 { font-family: 'Plus Jakarta Sans', sans-serif; font-size: 22px; font-weight: 700; color: #1e293b; }
.info-description { color: #64748b; line-height: 1.6; margin-bottom: 24px; }
.info-features { display: flex; flex-direction: column; gap: 16px; margin-bottom: 24px; }
.feature-item { display: flex; gap: 12px; padding: 16px; background: #f8fafc; border-radius: 12px; }
.feature-icon { width: 40px; height: 40px; border-radius: 10px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; background: #dbeafe; color: #2563eb; }
.feature-icon.green { background: #d1fae5; color: #059669; }
.feature-icon.blue { background: #dbeafe; color: #2563eb; }
.feature-icon.purple { background: #ede9fe; color: #7c3aed; }
.feature-item strong { display: block; font-size: 14px; color: #1e293b; margin-bottom: 2px; }
.feature-item p { font-size: 13px; color: #64748b; margin: 0; }

/* Quick Links - Styled like Track Progress card */
.quick-links {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 24px;
}

.quick-links-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #475569;
  margin-bottom: 4px;
}

.quick-link-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 18px;
  background: #f8fafc;
  border: 1.5px solid #e2e8f0;
  border-radius: 14px;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.25s ease;
}

.quick-link-item:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.06);
}

.quick-link-item.active-link {
  background: #eff6ff;
  border-color: #bfdbfe;
}

.quick-link-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.quick-link-icon.print {
  background: #dbeafe;
  color: #2563eb;
}

.quick-link-icon.apply {
  background: #d1fae5;
  color: #059669;
}

.quick-link-content {
  flex: 1;
  min-width: 0;
}

.quick-link-content strong {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 2px;
}

.quick-link-content p {
  font-size: 12px;
  color: #64748b;
  margin: 0;
  line-height: 1.4;
}

.quick-link-arrow {
  flex-shrink: 0;
  color: #94a3b8;
  transition: all 0.2s ease;
}

.quick-link-item:hover .quick-link-arrow {
  color: #475569;
  transform: translateX(3px);
}

.who-needs { padding: 20px; background: #fef3c7; border: 1px solid #fcd34d; border-radius: 12px; margin-bottom: 20px; }
.who-needs h4 { display: flex; align-items: center; gap: 8px; font-size: 14px; font-weight: 600; color: #92400e; margin-bottom: 12px; }
.who-needs ul { list-style: none; padding: 0; margin: 0; }
.who-needs li { font-size: 13px; color: #78350f; padding: 4px 0; padding-left: 16px; position: relative; }
.who-needs li::before { content: '•'; position: absolute; left: 0; color: #d97706; }

.help-contact { padding: 20px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; }
.help-contact h4 { display: flex; align-items: center; gap: 8px; font-size: 14px; font-weight: 600; color: #475569; margin-bottom: 12px; }
.contact-info { display: flex; flex-direction: column; gap: 6px; }
.contact-item { font-size: 14px; color: #64748b; }

/* Form Card */
.form-card { background: white; border-radius: 16px; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1); overflow: hidden; }
.form-card-header { padding: 24px 32px; background: #f8fafc; border-bottom: 1px solid #e2e8f0; display: flex; align-items: center; justify-content: space-between; }
.step-badge { display: flex; align-items: center; gap: 10px; }
.step-badge .step-number { width: 28px; height: 28px; background: #0f4c81; color: white; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: 600; font-size: 13px; }
.step-badge .step-text { font-weight: 600; color: #1e293b; }
.back-btn { display: flex; align-items: center; gap: 8px; padding: 8px 16px; background: none; border: 1px solid #e2e8f0; border-radius: 8px; color: #64748b; font-size: 14px; cursor: pointer; }
.back-btn:hover { background: #f1f5f9; color: #1e293b; }
.form-card-body { padding: 32px; }
.form-intro { color: #64748b; margin-bottom: 24px; line-height: 1.6; }

/* Alert */
.alert { display: flex; align-items: center; gap: 12px; padding: 14px 16px; border-radius: 10px; font-size: 14px; margin-bottom: 20px; }
.alert-error { background: #fef2f2; border: 1px solid #fecaca; color: #dc2626; }
.alert-close { margin-left: auto; background: none; border: none; cursor: pointer; padding: 4px; color: inherit; }

/* Method Selection */
.method-selection { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 24px; }
.method-card { padding: 24px; background: #f8fafc; border: 2px solid #e2e8f0; border-radius: 12px; cursor: pointer; transition: all 0.2s; display: flex; flex-direction: column; align-items: center; text-align: center; gap: 12px; }
.method-card:hover { border-color: #0f4c81; }
.method-card.selected { background: #dbeafe; border-color: #0f4c81; }
.hidden-radio, .hidden-checkbox { display: none; }
.method-icon { width: 64px; height: 64px; background: #dbeafe; border-radius: 16px; display: flex; align-items: center; justify-content: center; color: #0f4c81; }
.method-icon.green { background: #d1fae5; color: #059669; }
.method-content strong { display: block; font-size: 15px; color: #1e293b; margin-bottom: 4px; }
.method-content p { font-size: 13px; color: #64748b; margin: 0; }

/* Form Elements */
.form-group { margin-bottom: 20px; }
.form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.form-label { display: block; font-size: 14px; font-weight: 600; color: #1e293b; margin-bottom: 8px; }
.text-danger { color: #dc2626; }
.form-input, .form-textarea { width: 100%; padding: 14px 16px; border: 2px solid #e2e8f0; border-radius: 10px; font-family: 'DM Sans', sans-serif; font-size: 15px; transition: all 0.2s; background: white; }
.form-input:focus, .form-textarea:focus { outline: none; border-color: #0f4c81; box-shadow: 0 0 0 4px rgba(15, 76, 129, 0.1); }
.select-wrapper { position: relative; }
.form-select { width: 100%; padding: 14px 44px 14px 16px; border: 2px solid #e2e8f0; border-radius: 10px; font-family: 'DM Sans', sans-serif; font-size: 15px; background: white; cursor: pointer; appearance: none; }
.form-select:focus { outline: none; border-color: #0f4c81; box-shadow: 0 0 0 4px rgba(15, 76, 129, 0.1); }
.form-select:disabled { background: #f8fafc; cursor: not-allowed; }
.select-icon { position: absolute; right: 14px; top: 50%; transform: translateY(-50%); color: #94a3b8; pointer-events: none; }

.input-with-button { display: flex; gap: 8px; }
.input-with-button .form-input { flex: 1; }
.fetch-btn { padding: 14px 24px; background: #0f4c81; color: white; border: none; border-radius: 10px; font-weight: 600; cursor: pointer; transition: background 0.2s; }
.fetch-btn:hover:not(:disabled) { background: #0a3459; }
.fetch-btn:disabled { opacity: 0.6; cursor: not-allowed; }

/* Business Found Card */
.business-found-card { background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%); border: 1px solid #a7f3d0; border-radius: 12px; padding: 20px; margin-top: 20px; }
.business-found-header { display: flex; align-items: center; gap: 12px; margin-bottom: 16px; padding-bottom: 16px; border-bottom: 1px solid #a7f3d0; }
.found-icon { width: 44px; height: 44px; background: #10b981; border-radius: 10px; display: flex; align-items: center; justify-content: center; color: white; }
.business-found-header h4 { font-size: 16px; font-weight: 700; color: #065f46; margin: 0; }
.business-found-header p { font-size: 14px; color: #047857; margin: 0; font-weight: 600; }
.business-details-grid { display: flex; flex-direction: column; gap: 10px; }
.detail-row { display: flex; justify-content: space-between; padding: 8px 12px; background: white; border-radius: 8px; }
.detail-label { font-size: 13px; color: #6b7280; }
.detail-value { font-size: 14px; font-weight: 600; color: #1e293b; }

/* Not Found Card */
.not-found-card { text-align: center; padding: 40px 20px; background: #fef2f2; border: 1px solid #fecaca; border-radius: 12px; margin-top: 20px; }
.not-found-icon { color: #f87171; margin-bottom: 16px; }
.not-found-card h4 { color: #dc2626; margin-bottom: 8px; }
.not-found-card p { color: #7f1d1d; margin-bottom: 20px; }

/* Business Summary */
.business-summary { display: flex; align-items: center; gap: 14px; padding: 16px; background: #f8fafc; border-radius: 12px; margin-bottom: 24px; }
.summary-icon { width: 48px; height: 48px; background: #dbeafe; border-radius: 12px; display: flex; align-items: center; justify-content: center; color: #0f4c81; }
.business-summary strong { display: block; font-size: 16px; color: #1e293b; }
.business-summary p { font-size: 13px; color: #64748b; margin: 0; }

/* Other Charges */
.other-charges { margin-top: 24px; padding: 20px; background: #f8fafc; border-radius: 12px; }
.other-charges h4 { font-size: 15px; font-weight: 600; color: #1e293b; margin-bottom: 4px; }
.charges-hint { font-size: 13px; color: #64748b; margin-bottom: 16px; }
.charges-list { display: flex; flex-direction: column; gap: 8px; }
.charge-item { display: flex; align-items: center; gap: 12px; padding: 12px 16px; background: white; border: 2px solid #e2e8f0; border-radius: 10px; cursor: pointer; transition: all 0.2s; }
.charge-item:hover { border-color: #0f4c81; }
.charge-item.selected { background: #dbeafe; border-color: #0f4c81; }
.checkbox-mark { width: 20px; height: 20px; border: 2px solid #d1d5db; border-radius: 4px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; transition: all 0.2s; }
.charge-item.selected .checkbox-mark { background: #0f4c81; border-color: #0f4c81; color: white; }
.charge-name { flex: 1; font-size: 14px; color: #1e293b; }
.charge-amount { font-size: 14px; font-weight: 600; color: #0f4c81; }

/* Fee Summary */
.fee-summary { margin-top: 24px; padding: 20px; background: linear-gradient(135deg, #0f4c81 0%, #0a3459 100%); border-radius: 12px; color: white; }
.fee-row { display: flex; justify-content: space-between; padding: 8px 0; font-size: 14px; }
.fee-row.total { border-top: 1px solid rgba(255,255,255,0.2); margin-top: 8px; padding-top: 16px; font-size: 18px; font-weight: 700; }

/* Other Applicable Charges Section */
.other-charges-section { margin-top: 24px; padding: 24px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; }
.section-header-bar { display: flex; align-items: center; gap: 12px; margin-bottom: 20px; padding-bottom: 16px; border-bottom: 2px solid #0f4c81; }
.header-icon { width: 36px; height: 36px; background: #dbeafe; border-radius: 8px; display: flex; align-items: center; justify-content: center; color: #0f4c81; }
.section-header-bar h4 { font-size: 16px; font-weight: 700; color: #1e293b; margin: 0; flex: 1; }
.charges-count { font-size: 12px; color: #64748b; background: #e2e8f0; padding: 4px 10px; border-radius: 20px; }

.add-charge-form { display: flex; flex-direction: column; gap: 16px; }
.charge-step { display: flex; flex-direction: column; gap: 8px; }
.charge-step-row { display: flex; gap: 16px; align-items: flex-end; }
.quantity-step { flex: 0 0 120px; }
.step-badge { display: inline-block; font-size: 11px; font-weight: 600; padding: 4px 10px; border-radius: 20px; }
.step-badge.blue { background: #dbeafe; color: #1d4ed8; }
.step-badge.green { background: #d1fae5; color: #059669; }
.step-badge.purple { background: #ede9fe; color: #7c3aed; }

.btn-add-charge { display: flex; align-items: center; gap: 8px; padding: 14px 24px; background: #059669; color: white; border: none; border-radius: 10px; font-weight: 600; cursor: pointer; transition: all 0.2s; white-space: nowrap; }
.btn-add-charge:hover:not(:disabled) { background: #047857; }
.btn-add-charge:disabled { opacity: 0.5; cursor: not-allowed; }

.loading-charges { display: flex; align-items: center; gap: 10px; padding: 16px; background: #fef3c7; border-radius: 8px; color: #92400e; font-size: 14px; }
.spinner.blue { border-color: rgba(30, 64, 175, 0.2); border-top-color: #1d4ed8; }

/* Added Charges Table */
.added-charges-table { margin-top: 20px; overflow: hidden; border-radius: 10px; border: 1px solid #e2e8f0; }
.charges-table { width: 100%; border-collapse: collapse; }
.charges-table th, .charges-table td { padding: 12px 16px; text-align: left; }
.charges-table thead { background: #059669; color: white; }
.charges-table thead th { font-size: 13px; font-weight: 600; }
.charges-table tbody tr { border-bottom: 1px solid #e2e8f0; }
.charges-table tbody tr:last-child { border-bottom: none; }
.charges-table tbody td { font-size: 14px; color: #1e293b; background: white; }
.charges-table tfoot { background: #f1f5f9; }
.charges-table tfoot th { font-size: 14px; font-weight: 700; color: #059669; }
.text-center { text-align: center; }
.text-right { text-align: right; }

.remove-charge-btn { padding: 6px 8px; background: #fef2f2; border: 1px solid #fecaca; border-radius: 6px; color: #dc2626; cursor: pointer; transition: all 0.2s; }
.remove-charge-btn:hover { background: #fee2e2; }

/* Summary Section */
.summary-section { margin-bottom: 24px; padding-bottom: 24px; border-bottom: 1px solid #e2e8f0; }
.summary-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; }
.summary-header h4 { font-size: 16px; font-weight: 600; color: #1e293b; }
.edit-btn { padding: 6px 14px; background: #f1f5f9; border: 1px solid #e2e8f0; border-radius: 6px; font-size: 13px; color: #0f4c81; cursor: pointer; }
.edit-btn:hover { background: #e2e8f0; }
.summary-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; }
.summary-item { padding: 12px; background: #f8fafc; border-radius: 8px; }
.summary-label { display: block; font-size: 11px; color: #64748b; text-transform: uppercase; margin-bottom: 4px; }
.summary-value { font-size: 14px; font-weight: 500; color: #1e293b; }

/* Invoice Preview */
.invoice-preview { margin-bottom: 24px; }
.invoice-preview h4 { font-size: 16px; font-weight: 600; color: #1e293b; margin-bottom: 12px; }
.invoice-table { width: 100%; border-collapse: collapse; }
.invoice-table th, .invoice-table td { padding: 12px 16px; text-align: left; border: 1px solid #e2e8f0; }
.invoice-table thead { background: #f8fafc; }
.invoice-table thead th { font-size: 13px; font-weight: 600; color: #475569; }
.invoice-table tbody td { font-size: 14px; color: #1e293b; }
.invoice-table tfoot { background: #0f4c81; color: white; }
.invoice-table tfoot th { font-size: 15px; }

/* Payment Notice */
.payment-notice { display: flex; gap: 16px; padding: 20px; background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 12px; margin-bottom: 24px; }
.notice-icon { color: #2563eb; flex-shrink: 0; }
.payment-notice strong { display: block; color: #1e40af; margin-bottom: 4px; }
.payment-notice p { font-size: 14px; color: #3b82f6; margin: 0; }

/* Declaration */
.declaration-box { padding: 20px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; margin-bottom: 24px; }
.declaration-label { display: flex; align-items: flex-start; gap: 12px; cursor: pointer; }
.declaration-label input { display: none; }
.declaration-label .checkbox-mark { width: 22px; height: 22px; border: 2px solid #d1d5db; border-radius: 6px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; }
.declaration-label input:checked + .checkbox-mark { background: #0f4c81; border-color: #0f4c81; color: white; }
.declaration-text { font-size: 14px; color: #475569; line-height: 1.6; }

/* Buttons */
.btn { display: inline-flex; align-items: center; justify-content: center; gap: 10px; padding: 14px 24px; border-radius: 10px; font-family: 'DM Sans', sans-serif; font-size: 15px; font-weight: 600; cursor: pointer; border: none; text-decoration: none; transition: all 0.2s; }
.btn-lg { padding: 16px 32px; font-size: 16px; }
.btn-primary { background: #0f4c81; color: white; }
.btn-primary:hover:not(:disabled) { background: #0a3459; }
.btn-success { background: #059669; color: white; }
.btn-success:hover:not(:disabled) { background: #047857; }
.btn-secondary { background: #f1f5f9; color: #475569; border: 1px solid #e2e8f0; }
.btn-secondary:hover { background: #e2e8f0; }
.btn-outline { background: transparent; color: #475569; border: 1px solid #e2e8f0; }
.btn-outline:hover { background: #f8fafc; }
.btn:disabled { opacity: 0.6; cursor: not-allowed; }
.w-100 { width: 100%; }
.mt-3 { margin-top: 12px; }
.form-actions { display: flex; justify-content: flex-end; margin-top: 24px; padding-top: 24px; border-top: 1px solid #e2e8f0; }

/* Spinner */
.spinner { width: 20px; height: 20px; border: 2px solid rgba(255,255,255,0.3); border-top-color: white; border-radius: 50%; animation: spin 0.8s linear infinite; }
.spinner.small { width: 16px; height: 16px; border-color: rgba(255,255,255,0.3); border-top-color: white; }
.spinner.white { border-color: rgba(255,255,255,0.3); border-top-color: white; }
@keyframes spin { to { transform: rotate(360deg); } }

/* Success Card */
.success-card .form-card-body { padding: 48px; }
.success-content { text-align: center; }
.success-icon { width: 80px; height: 80px; background: #d1fae5; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: #059669; margin: 0 auto 24px; }
.success-content h2 { font-family: 'Plus Jakarta Sans', sans-serif; font-size: 24px; font-weight: 700; color: #059669; margin-bottom: 8px; }
.success-message { color: #64748b; margin-bottom: 32px; }

.invoice-card { background: linear-gradient(135deg, #0f4c81 0%, #0a3459 100%); border-radius: 14px; padding: 24px; color: white; margin-bottom: 24px; }
.invoice-label { font-size: 12px; opacity: 0.8; text-transform: uppercase; }
.invoice-number { display: block; font-size: 24px; font-weight: 800; margin: 8px 0 16px; letter-spacing: 1px; }
.invoice-amount { display: flex; justify-content: space-between; padding-top: 16px; border-top: 1px solid rgba(255,255,255,0.2); }
.invoice-amount span { opacity: 0.8; }
.invoice-amount strong { font-size: 20px; }

.notification-card { display: flex; align-items: flex-start; gap: 16px; padding: 20px; background: #fef3c7; border: 1px solid #fcd34d; border-radius: 12px; margin-bottom: 24px; text-align: left; }
.notification-icon { width: 44px; height: 44px; background: #fbbf24; border-radius: 10px; display: flex; align-items: center; justify-content: center; color: white; flex-shrink: 0; }
.notification-text strong { display: block; color: #92400e; margin-bottom: 4px; }
.notification-text p { font-size: 14px; color: #a16207; margin: 0; }

.next-steps { text-align: left; margin-bottom: 32px; }
.next-steps h4 { font-size: 16px; font-weight: 600; color: #1e293b; margin-bottom: 16px; text-align: center; }
.next-steps ul { list-style: none; padding: 0; margin: 0; }
.next-steps li { display: flex; gap: 16px; padding: 16px; background: #f8fafc; border-radius: 10px; margin-bottom: 8px; }
.step-num { width: 28px; height: 28px; background: #0f4c81; color: white; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 13px; flex-shrink: 0; }
.next-steps li strong { display: block; color: #1e293b; margin-bottom: 2px; }
.next-steps li p { font-size: 13px; color: #64748b; margin: 0; }

.success-actions { display: flex; gap: 12px; justify-content: center; flex-wrap: wrap; }

/* Transitions */
.slide-fade-enter-active { transition: all 0.3s ease; }
.slide-fade-leave-active { transition: all 0.2s ease; }
.slide-fade-enter-from { opacity: 0; transform: translateX(20px); }
.slide-fade-leave-to { opacity: 0; transform: translateX(-20px); }

/* Responsive */
@media (max-width: 1024px) {
  .content-grid { grid-template-columns: 1fr; }
  .info-panel { position: static; }
  .progress-stepper { flex-wrap: wrap; gap: 16px; }
  .step-connector { display: none; }
}

@media (max-width: 768px) {
  .form-card-body { padding: 24px; }
  .form-row { grid-template-columns: 1fr; }
  .method-selection { grid-template-columns: 1fr; }
  .summary-grid { grid-template-columns: 1fr; }
  .success-actions { flex-direction: column; }
  .page-title { font-size: 26px; }
  .charge-step-row { flex-direction: column; }
  .quantity-step { flex: 1; }
  .btn-add-charge { width: 100%; justify-content: center; }
  .charges-table { font-size: 13px; }
  .charges-table th, .charges-table td { padding: 10px 12px; }
}
</style>

